import { useState } from "react";
import { Link } from "react-router-dom";
import { api, useApi } from "../api.js";
import { ModelSelect, Notice, useToast } from "../components.js";
import { usePermission } from "../auth-context.js";

interface CollectionRow {
  id: number;
  name: string;
  description: string | null;
  model: string;
  vectorStoreId: string | null;
  createdByName?: string | null;
  sourceCount: number;
  categoryCount: number;
  indexedBytes: number;
  /** The one syllabus this collection covers (migration 020). */
  board: string | null;
  className: string | null;
  subject: string | null;
}

interface Named {
  id: string;
  name: string;
}

/** Only the identity is needed here — the chapter list is irrelevant when all
 *  this screen does is bind a collection to a syllabus. */
interface SyllabusIdentity {
  id: string;
}

/**
 * Collections are the unit of knowledge and configuration: each owns exactly
 * one vector store (a request can search only one) plus its own categories,
 * copied from a starter set on creation and free to diverge after that.
 */
export function CollectionsList() {
  const canCreate = usePermission("collection:create");
  const [refreshKey, setRefreshKey] = useState(0);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [model, setModel] = useState("gpt-4.1");
  const [openaiKeyName, setOpenaiKeyName] = useState("");
  const [formResetKey, setFormResetKey] = useState(0);
  // Required (migration 020): one collection covers one syllabus, which is
  // what lets the Generation screen resolve it instead of asking.
  const [board, setBoard] = useState<string | null>(null);
  const [className, setClassName] = useState<string | null>(null);
  const [subject, setSubject] = useState<string | null>(null);
  /** Set when the form is copying an existing collection instead of creating
   *  an empty one. The syllabus picker and every other field are shared —
   *  only the endpoint and the button label differ. */
  const [cloneSource, setCloneSource] = useState<CollectionRow | null>(null);
  const { toast, element } = useToast();

  const collections = useApi<CollectionRow[]>("/collections", [refreshKey]);
  const keyNames = useApi<{ names: string[] }>("/collections/openai-keys");
  const models = useApi<string[]>("/models");
  const boards = useApi<Named[]>("/boards");
  const classes = useApi<Named[]>("/classes");
  const subjects = useApi<Named[]>("/subjects");

  // Fetched only to get the main app's syllabus id for the chosen trio —
  // that id, not the three names, is what the collection is keyed on.
  const syllabus = useApi<SyllabusIdentity>(
    board && className && subject
      ? `/chapter-tree?board=${encodeURIComponent(board)}&class=${encodeURIComponent(className)}&subject=${encodeURIComponent(subject)}`
      : null,
    [board, className, subject],
  );

  const takenBy = collections.data?.find(
    (c) => c.board === board && c.className === className && c.subject === subject,
  );

  async function create() {
    if (!name.trim()) {
      toast("Name is required");
      return;
    }
    if (!board || !className || !subject) {
      toast("Choose the syllabus this collection covers");
      return;
    }
    if (!syllabus.data) {
      toast("That board/class/subject has no syllabus in the main app");
      return;
    }
    setCreating(true);
    try {
      if (cloneSource) {
        // The clone endpoint takes its model and general-knowledge switch from
        // the source, so neither is sent here — showing the Model field in
        // clone mode would imply it does something.
        const result = await api.post<{ categoriesCopied: number; pipelinesCopied: number }>(
          `/collections/${cloneSource.id}/clone`,
          {
            name: name.trim(),
            description: description.trim() || null,
            syllabus: { syllabusId: syllabus.data.id, board, className, subject },
          },
        );
        resetForm();
        const copied =
          `${result.categoriesCopied} categor${result.categoriesCopied === 1 ? "y" : "ies"}` +
          (result.pipelinesCopied > 0
            ? ` and ${result.pipelinesCopied} pipeline${result.pipelinesCopied === 1 ? "" : "s"}`
            : "");
        toast(`Cloned ${copied} — upload knowledge sources for the new syllabus`);
      } else {
        await api.post("/collections", {
          name: name.trim(),
          description: description.trim() || null,
          model: model.trim() || undefined,
          openaiKeyName: openaiKeyName || null,
          syllabus: { syllabusId: syllabus.data.id, board, className, subject },
        });
        resetForm();
        toast("Collection created with starter categories");
      }
    } catch (error: unknown) {
      toast(error instanceof Error ? error.message : "Failed to save collection");
    } finally {
      setCreating(false);
    }
  }

  function resetForm() {
    setName("");
    setDescription("");
    setModel("gpt-4.1");
    setOpenaiKeyName("");
    setBoard(null);
    setClassName(null);
    setSubject(null);
    setCloneSource(null);
    setFormResetKey((k) => k + 1);
    setRefreshKey((k) => k + 1);
  }

  /** Switches the form into clone mode, pre-filled from the source. The
   *  syllabus is deliberately left blank: it is the one thing that MUST
   *  differ, since one collection covers one syllabus. */
  function startClone(source: CollectionRow) {
    setCloneSource(source);
    setName(`${source.name} (copy)`);
    setDescription(source.description ?? "");
    setBoard(null);
    setClassName(null);
    setSubject(null);
    setFormResetKey((k) => k + 1);
  }

  return (
    <>
      <div className="grid">
        <div className="card span-8">
          <div className="row between">
            <h2>Collections</h2>
          </div>

          {collections.error && <Notice kind="error">Cannot load collections: {collections.error}</Notice>}
          {collections.loading && !collections.data && <p className="muted">Loading…</p>}
          {collections.data?.length === 0 && (
            <p className="muted">No collections yet — create the first one.</p>
          )}

          {collections.data?.map((c) => (
            <Link key={c.id} to={`/collections/${c.id}`} className="book selectable" style={{ display: "block" }}>
              <div className="row between">
                <b>{c.name}</b>
                <span className={`badge ${c.vectorStoreId ? "generated" : "pending"}`}>
                  {c.vectorStoreId ? "Indexed" : "No knowledge yet"}
                </span>
              </div>
              {c.description && <div className="muted">{c.description}</div>}
              <div className="mini muted" style={{ marginTop: 6 }}>
                {c.sourceCount} source{c.sourceCount === 1 ? "" : "s"} · {c.categoryCount} categor
                {c.categoryCount === 1 ? "y" : "ies"} · {c.model}
                {c.indexedBytes > 0 ? ` · ${(c.indexedBytes / 1024 / 1024).toFixed(1)} MB indexed` : ""}
                {c.createdByName && ` · Author: ${c.createdByName}`}
              </div>
              <button
                className="btn mini"
                style={{ marginTop: 8 }}
                onClick={(e) => {
                  // The whole row is a Link — without both of these, cloning
                  // also navigates into the collection.
                  e.preventDefault();
                  e.stopPropagation();
                  startClone(c);
                }}
              >
                Clone
              </button>
            </Link>
          ))}
        </div>

        {/* Hidden outright rather than disabled: someone who cannot create a
            collection has no use for the syllabus pickers either, and a dead
            panel taking a third of the screen is worse than no panel. */}
        {canCreate && (
        <div className="card span-4">
          <h2>{cloneSource ? "Clone collection" : "New collection"}</h2>

          {cloneSource && (
            <Notice kind="info">
              Copying <b>{cloneSource.name}</b> — its categories and model come with it.
              Knowledge sources do not: a PDF is scoped to its own syllabus&rsquo;s chapters, so
              upload them again for the new one.{" "}
              <button className="btn mini" onClick={() => resetForm()}>
                Cancel
              </button>
            </Notice>
          )}
          <div className="field">
            <label>Name</label>
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. 10th Science NCERT" />
          </div>
          <div className="field">
            <label>Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              style={{ minHeight: 80 }}
              placeholder="Optional"
            />
          </div>
          {/* Not shown when cloning: the copy takes its model from the source,
              so an editable field here would imply a choice that is ignored. */}
          {!cloneSource && (
            <div className="field">
              <label>Model</label>
              <ModelSelect key={formResetKey} value={model} onChange={setModel} models={models.data ?? []} />
            </div>
          )}
          {/* Only offered when more than the default key is configured, and not
              when cloning: the copy keeps the source's key. */}
          {!cloneSource && (keyNames.data?.names.length ?? 0) > 0 && (
            <div className="field">
              <label>OpenAI key</label>
              <select value={openaiKeyName} onChange={(e) => setOpenaiKeyName(e.target.value)}>
                <option value="">Default key</option>
                {(keyNames.data?.names ?? []).map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
              <div className="mini muted">
                Fixed once the collection has knowledge sources or documents — they live under this key.
              </div>
            </div>
          )}

          <div className="field">
            <label>Syllabus</label>
            <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
              <select value={board ?? ""} onChange={(e) => setBoard(e.target.value || null)}>
                <option value="">Board…</option>
                {boards.data?.map((b) => (
                  <option key={b.id} value={b.name}>
                    {b.name}
                  </option>
                ))}
              </select>
              <select value={className ?? ""} onChange={(e) => setClassName(e.target.value || null)}>
                <option value="">Class…</option>
                {classes.data?.map((c) => (
                  <option key={c.id} value={c.name}>
                    {c.name}
                  </option>
                ))}
              </select>
              <select value={subject ?? ""} onChange={(e) => setSubject(e.target.value || null)}>
                <option value="">Subject…</option>
                {subjects.data?.map((sub) => (
                  <option key={sub.id} value={sub.name}>
                    {sub.name}
                  </option>
                ))}
              </select>
            </div>
            <span className="mini muted">
              Fixed once created — the Generation screen uses it to find this collection, and
              knowledge sources are mapped to its chapters.
              {cloneSource && " Pick a different syllabus from the one being copied."}
            </span>
            {takenBy && (
              <Notice kind="error">
                &quot;{takenBy.name}&quot; already covers that syllabus.
              </Notice>
            )}
            {board && className && subject && !syllabus.loading && !syllabus.data && (
              <Notice kind="warn">The main app has no syllabus for that combination.</Notice>
            )}
          </div>

          <button
            className="btn primary"
            onClick={() => void create()}
            disabled={creating || Boolean(takenBy) || !syllabus.data}
          >
            {creating
              ? cloneSource
                ? "Cloning…"
                : "Creating…"
              : cloneSource
                ? "Clone Collection"
                : "Create Collection"}
          </button>
          <p className="muted" style={{ marginTop: 8 }}>
            {cloneSource
              ? "The copy needs its own knowledge sources before it can generate."
              : "Starts with no categories — add them from the collection's Categories tab."}
          </p>
        </div>
        )}
      </div>

      {element}
    </>
  );
}
